import React from 'react'
import clsx from 'clsx'
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete'
import { Add } from '@mui/icons-material'
import { SearchNormal } from 'iconsax-react'
import { useTheme } from '@mui/material'
import { useAppDispatch, useAppSelector } from '@SonarCX/hooks/redux'

import { track } from '@SonarCX/lib/mixpanel'
import { addTag, fetchTags, selectTag } from '@SonarCX/slices/tag'
import useAccess, { PERMS } from '@SonarCX/hooks/useAccess'
import useStyles, { StyledTextField } from './styles'

const filter = createFilterOptions()

const TagInputV2 = ({
  id = `input-tags`,
  testid = 'test-tag-input',
  handleAddTag,
  label,
  placeholder = `Type to search or add new tag ...`,
  inputClass,
  height,
  width,
  alwaysShowInput = false,
  noAutofocus,
  customPlaceholder,
  onResetValueTrigger,
  disabled,
  className
}) => {
  const theme = useTheme()
  const dispatch = useAppDispatch()

  const { tags, tagsLoaded, tagsLoading } = useAppSelector(selectTag)

  const hasPermission = useAccess()

  const [isAdding, setIsAdding] = React.useState(alwaysShowInput ? true : false)
  const [value, setValue] = React.useState('')
  const [inputValue, setInputValue] = React.useState('')
  const tagInputRef = React.useRef(null)

  const classes = useStyles({ height, width, customPlaceholder })

  React.useEffect(() => {
    if (!tagsLoaded && !tagsLoading) {
      dispatch(fetchTags())
    }
  }, [tagsLoaded, tagsLoading, dispatch])

  React.useEffect(() => {
    if (onResetValueTrigger) {
      setValue('')
    }
  }, [onResetValueTrigger])

  const canAddTags = React.useMemo(
    () => hasPermission(PERMS.TagsAdmin),
    [hasPermission]
  )

  const handleKeyDown = React.useCallback(
    e => {
      if (e.key === 'Escape' && !alwaysShowInput) {
        setIsAdding(false)
      }
    },
    [alwaysShowInput]
  )

  const createTag = React.useCallback(
    tag => {
      if (!tag || !canAddTags) {
        return
      }

      // Create a new tag in the server from the user input
      track('TAG_CREATED', { tag })
      dispatch(addTag(tag))
    },
    [canAddTags, dispatch]
  )

  const handleTagChange = React.useCallback(
    newValue => {
      const performUpdate = tag => {
        handleAddTag(tag)
        setValue('')
        setInputValue('')
        createTag(tag)

        if (!alwaysShowInput) {
          setIsAdding(false)
        }
      }

      if (typeof newValue === 'string') {
        performUpdate(newValue)
        return
      }

      if (newValue && newValue.custom) {
        performUpdate(newValue.inputValue)
        return
      }

      performUpdate(newValue.value)
    },
    [alwaysShowInput, createTag, handleAddTag]
  )

  const filterOptions = React.useCallback(
    (options, params) => {
      const { inputValue } = params
      const filtered = filter(options, params)

      // Suggest the creation of a new value
      const isExisting = options.some(option => inputValue === option)
      if (inputValue !== '' && !isExisting) {
        filtered.unshift({
          inputValue,
          value: canAddTags
            ? `Create tag: "${inputValue}"`
            : `Select tag: "${inputValue}"`,
          create: canAddTags,
          custom: true
        })
      }

      return filtered
    },
    [canAddTags]
  )

  if (!isAdding) {
    return (
      <div
        className={clsx(classes.box, classes.addTagBox)}
        onClick={() => setIsAdding(true)}
      >
        <Add fill='#8F8F8F' className={clsx(classes.icon, classes.muiIcon)} />
        Add tags
      </div>
    )
  }

  return (
    <div className={clsx(classes.root, className)}>
      <Autocomplete
        id={id}
        data-testid={testid}
        value={value}
        options={tags}
        onChange={(_event, newValue) => handleTagChange(newValue)}
        inputValue={inputValue}
        onInputChange={(event, newInputValue) => setInputValue(newInputValue)}
        filterOptions={(options, params) => filterOptions(options, params)}
        renderInput={params => (
          <StyledTextField
            {...params}
            type='text'
            size='small'
            variant='outlined'
            label={label}
            onKeyDown={handleKeyDown}
            inputRef={tagInputRef}
            autoFocus={!noAutofocus}
            placeholder={placeholder}
            className={inputClass}
            inputProps={{
              ...params.inputProps,
              maxLength: 25
            }}
          />
        )}
        getOptionLabel={option => {
          //don't display anything on select, just add it to list
          return option?.value || option
        }}
        className={classes.textField}
        freeSolo
        selectOnFocus
        disableClearable
        includeInputInList
        disabled={disabled}
      />
      <SearchNormal size={theme.size.sm} className={classes.inputIcon} />
    </div>
  )
}

export default TagInputV2
